/**
 * Real-time list of the signed-in user's client profiles.
 *
 * Subscribes to `users/{uid}/clients` ordered by `name`. These profiles power
 * the "quem é o cliente" briefings and mirror the agent's `upsertClientProfile`
 * tool.
 */
import type { ClientProfileDoc } from '@agente/shared'
import { onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { clientsCol } from '@/lib/firestore'

export interface UseClientsResult {
  clients: ClientProfileDoc[]
  loading: boolean
  error: string | null
}

export function useClients(): UseClientsResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [clients, setClients] = useState<ClientProfileDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setClients([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const q = query(clientsCol(uid), orderBy('name', 'asc'))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setClients(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { clients, loading, error }
}
